// poAuditHelpers — shared status enum + presentation helpers for the
// two-tier PO audit view (Ambiguous + Tier 1 total-mismatch).
//
// A "vendor row" inside one PO has exactly one status:
//   match     — qbo_total ≈ tek_loaded (within tolerance)
//   over      — qbo_total > tek_loaded (over-billed; vendor charged us too much)
//   under     — qbo_total < tek_loaded (under-billed; we paid less than received)
//   qbo_only  — vendor has QBO bills but nothing on Tekmetric PO
//   tek_only  — vendor has Tek pops but nothing billed yet on QBO

import {
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  PlusCircle,
  MinusCircle,
} from 'lucide-react';

export const STATUS_MATCH = 'match';
export const STATUS_OVER = 'over';
export const STATUS_UNDER = 'under';
export const STATUS_QBO_ONLY = 'qbo_only';
export const STATUS_TEK_ONLY = 'tek_only';

export function statusLabel(status) {
  switch (status) {
    case STATUS_MATCH:    return 'Matched';
    case STATUS_OVER:     return 'Over-billed';
    case STATUS_UNDER:    return 'Under-billed';
    case STATUS_QBO_ONLY: return 'QBO only';
    case STATUS_TEK_ONLY: return 'Tek only';
    default:              return status || '—';
  }
}

export function statusBadgeTone(status) {
  switch (status) {
    case STATUS_MATCH:    return 'recovery';
    case STATUS_OVER:     return 'danger';
    case STATUS_UNDER:    return 'action';
    case STATUS_QBO_ONLY: return 'insight';
    case STATUS_TEK_ONLY: return 'cream';
    default:              return 'neutral';
  }
}

// Returns the lucide icon component (not an element) so the caller can
// render it with its own sizing/color.
export function statusIcon(status) {
  switch (status) {
    case STATUS_MATCH:    return CheckCircle2;
    case STATUS_OVER:     return TrendingUp;
    case STATUS_UNDER:    return TrendingDown;
    case STATUS_QBO_ONLY: return PlusCircle;
    case STATUS_TEK_ONLY: return MinusCircle;
    default:              return CheckCircle2;
  }
}

// Format a signed delta in cents → "+$80.21" / "−$19.80" / "$0".
// Uses the proper minus sign (U+2212) for visual symmetry with +.
export function formatDelta(cents) {
  if (!cents) return '$0';
  const sign = cents > 0 ? '+' : '−';
  const abs = Math.abs(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sign}$${abs}`;
}

// Count "issue" vendors from a status_summary block — anything that isn't
// fully matched. Used for the PO-row header chip "N issues".
export function countIssues(statusSummary) {
  if (!statusSummary) return 0;
  return (
    (statusSummary.vendors_over     || 0) +
    (statusSummary.vendors_under    || 0) +
    (statusSummary.vendors_qbo_only || 0) +
    (statusSummary.vendors_tek_only || 0)
  );
}
